import {
  Typography,
  Box,
  Theme,
  useMediaQuery,
  IconButton,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  DataGridPremium,
  type GridApiPremium,
  GridColDef,
  GridValidRowModel,
  GridRenderCellParams,
  GridInitialState,
  GridTreeDataGroupingCell,
  type GridGroupNode,
  type GridListViewColDef,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { createExcelExportWorker } from '@/utils/excelExportWorker';
import {
  aggregateHistoricalComparisons,
  formatHistoricalCurrency,
  lookupLiveEndingBalance,
  withHistoricalChangeForRange,
  withLiveEndingBalance,
} from '@/utils/historicalBalance';
import { useGetAccountHistoricalComparisonQuery } from '@/store/accountsApi';
import {
  historicalComparisonQueryArg,
  isCustomGroupBy,
} from './historicalComparisonQueryArg';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { APP_DATE_DISPLAY_FORMAT } from '@/constants/dateFormats';
import { formatAppDate } from '@/utils/date';
import { filterByInScopeIds } from '@/utils/historicalAccountScope';
import { DataRangeFilterEnum } from '@/constants/dates';
import { useLocalization } from '@/hooks/useLocalization';
import { useAccountCreatedDates } from '@/hooks/useAccountCreatedDates';
import { useInScopeAccountIds } from '@/hooks/useInScopeAccountIds';
import { mobileWidth } from '@/constants';
import { GRID_INITIAL_PAGINATION } from '@/constants/pagination';
import { AccountHistoricalComparisonItem } from '@/models/AccountViewModel';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { Capacitor } from '@capacitor/core';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { formatPercentValue } from '@/utils/numbers';
import {
  GridContainer,
  type GridContainerSize,
} from '@/components/grids/GridContainer';

interface GridRow extends GridValidRowModel {
  id: string;
  Name: string;
  groupName: string;
  groupSort: string | number;
  isGroupTotal?: boolean;
  sortIndex: number;
  isAsset: boolean;
  classificationSort: number;
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
  AccountIcon?: string;
}

interface TreeGridRow extends GridRow {
  parentId?: string | null;
  path?: string[];
  isGrandTotal?: boolean;
}

interface GrandTotal {
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
}

type BalanceField = 'OldDollarBalance' | 'DollarBalance' | 'DollarChange';

interface GroupTotals {
  OldDollarBalance: number | null;
  DollarBalance: number | null;
  DollarChange: number | null;
  PercentChange: number | null;
  count: number;
}

type GroupTotalsByName = Map<string, GroupTotals>;

type GroupNodeWithCounts = GridGroupNode & {
  descendantCount?: number;
  childrenIds?: Array<string | number>;
};

interface HistoricalGridProps {
  dateFilter: DataRangeFilterEnum;
  startDate?: string;
  endDate?: string;
  accountId?: number;
  /** Accounts-page balance for `accountId` when the accounts list omits it. */
  liveBalance?: number;
  isWidget?: boolean;
  size?: GridContainerSize;
  groupBy?: string;
  excludedAccounts?: number[];
  excludedAccountTypes?: number[];
  excludedCategories?: number[];
  excludedInstitutions?: number[];
}

// Constants
const INITIAL_GRID_STATE_GROUPED: GridInitialState = {
  sorting: {
    sortModel: [
      { field: 'groupName', sort: 'asc' },
      { field: 'sortIndex', sort: 'asc' },
    ],
  },
  aggregation: {
    model: {
      OldDollarBalance: 'sum',
      DollarBalance: 'sum',
      DollarChange: 'sum',
    },
  },
  pagination: GRID_INITIAL_PAGINATION,
};

const gridStyles = {
  fontSize: '14px',
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme: Theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : theme.palette.grey[100],
    borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
  },
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
    fontSize: '14px',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(224, 224, 224, 0.2)',
    padding: '0px 10px 0px 10px',
    '&.MuiDataGrid-cell--tree': {
      paddingLeft: 0,
    },
  },
  '& .MuiDataGrid-row:last-child .MuiDataGrid-cell': {
    borderBottom: 'none',
  },
  '& .MuiDataGrid-row': {
    '&.MuiDataGrid-row--tree': {
      bgcolor: (theme: Theme) =>
        theme.palette.mode === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.grey[100],
    },
    '& .MuiDataGrid-treeDataGroupingCellToggle': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
};

const getAccountIcon = (data: GridRow) => {
  if (data.AccountIcon) {
    return <span className={`fa ${data.AccountIcon}`} />;
  }
  return null;
};

const groupKeyLabel = (key: GridGroupNode['groupingKey']): string => {
  if (key == null) return '';
  return typeof key === 'string' ? key : String(key);
};

const groupDescendantCount = (node: GridGroupNode): number => {
  const withCounts = node as GroupNodeWithCounts;
  return (
    withCounts.descendantCount ??
    withCounts.childrenIds?.length ??
    node.children.length
  );
};

const getGroupTotals = (
  totalsByName: GroupTotalsByName,
  groupName: string,
): GroupTotals | undefined => {
  return totalsByName.get(groupName);
};

const toDisplayString = (
  value: string | number | boolean | null | undefined,
): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

/**
 * Generated aggregation/footer rows are not TreeGridRows and omit Name.
 * The previous isGridRow guard used this same shape check before reading Name.
 */
const isAccountListRow = (row: TreeGridRow): boolean =>
  'id' in row && 'Name' in row && typeof row.Name === 'string';

const createBaseColumnProps = () => ({
  headerAlign: 'right' as const,
  align: 'right' as const,
});

const createNameColumn = (
  t: (key: string) => string,
): GridColDef<TreeGridRow> => ({
  field: 'Name',
  headerName: t('name'),
  flex: 1,
  minWidth: 300,
  renderCell: (params: GridRenderCellParams<TreeGridRow>) => {
    try {
      const { row, rowNode } = params;
      const isTotal = row.isGroupTotal;
      const isGrandTotal = row.isGrandTotal;

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            pl: rowNode.depth > 0 ? 2 : 0,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: isTotal || isGrandTotal ? 600 : 400,
              color: 'text.primary',
              flexGrow: 1,
            }}
          >
            {row.Name}
          </Typography>
          {!isTotal && getAccountIcon(row)}
        </Box>
      );
    } catch (error) {
      console.error('Error in Name renderCell:', error);
      return null;
    }
  },
});

const getValueColor = (value: number | null | undefined) => {
  if (value && value > 0) return 'success.main';
  if (value && value < 0) return 'error.main';
  return 'text.secondary';
};

const createBalanceColumn = (
  field: BalanceField,
  headerName: string,
  totalsByName: GroupTotalsByName,
  emptyLabel: string,
): GridColDef<TreeGridRow> => ({
  ...createBaseColumnProps(),
  field,
  headerName,
  width: GRID_COLUMN_WIDTH_NARROW,
  renderCell: (params: GridRenderCellParams<TreeGridRow, number>) => {
    if (params.rowNode.type === 'group') {
      const groupName = groupKeyLabel(params.rowNode.groupingKey);
      const value = getGroupTotals(totalsByName, groupName)?.[field];

      if (value !== undefined) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}
            >
              {formatHistoricalCurrency(value, emptyLabel)}
            </Typography>
          </Box>
        );
      }
      return null;
    }
    const value = params.value;
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: getValueColor(value) }}
        >
          {formatHistoricalCurrency(value, emptyLabel)}
        </Typography>
      </Box>
    );
  },
});

const createPercentageColumn = (
  t: (key: string, defaultVal: string) => string,
  totalsByName: GroupTotalsByName,
  emptyLabel: string,
): GridColDef<TreeGridRow> => ({
  ...createBaseColumnProps(),
  field: 'PercentChange',
  headerName: t('grid.percentChange', '$ Change'),
  width: GRID_COLUMN_WIDTH_NARROW,
  renderCell: (params: GridRenderCellParams<TreeGridRow, number>) => {
    if (params.rowNode.type === 'group') {
      const groupName = groupKeyLabel(params.rowNode.groupingKey);
      const value = getGroupTotals(totalsByName, groupName)?.PercentChange;

      if (value !== undefined) {
        return (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: getValueColor(value),
                fontWeight: 'bold',
              }}
            >
              {value == null ? emptyLabel : formatPercentValue(value)}
            </Typography>
          </Box>
        );
      }
      return null;
    }

    const value = params.value;

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: getValueColor(value) }}
        >
          {value == null ? emptyLabel : formatPercentValue(value)}
        </Typography>
      </Box>
    );
  },
});

const CustomFooter = ({
  grandTotal,
  emptyLabel,
}: {
  grandTotal: GrandTotal | null;
  emptyLabel: string;
}) => (
  <Box
    sx={{
      borderTop: (theme: Theme) => `2px solid ${theme.palette.divider}`,
      display: 'grid',
      gridTemplateColumns: 'minmax(300px, 1fr) repeat(4, 150px)',
      alignItems: 'center',
      px: 2,
      py: 2,
    }}
  >
    <Typography
      variant='body2'
      sx={{ fontWeight: 600, pl: 1 }}
    >
      Total:
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.OldDollarBalance),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.OldDollarBalance, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.DollarBalance),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.DollarBalance, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.DollarChange),
      }}
    >
      {formatHistoricalCurrency(grandTotal?.DollarChange, emptyLabel)}
    </Typography>
    <Typography
      variant='body2'
      sx={{
        fontWeight: 600,
        textAlign: 'right',
        pr: 2,
        color: getValueColor(grandTotal?.PercentChange),
      }}
    >
      {grandTotal?.PercentChange == null
        ? emptyLabel
        : formatPercentValue(grandTotal.PercentChange)}
    </Typography>
  </Box>
);

const HistoricalGrid = ({
  startDate,
  endDate,
  accountId,
  liveBalance,
  isWidget = false,
  size,
  groupBy = 'none',
  excludedAccounts = [],
  excludedAccountTypes = [],
  excludedCategories = [],
  excludedInstitutions = [],
}: HistoricalGridProps) => {
  const isMobileMatch = useMediaQuery(mobileWidth);
  const theme = useTheme();
  const { ref: widgetWidthRef, isBelow: isWidgetBelowXsWidth } =
    useWidgetWidthIsBelow(isWidget, theme.breakpoints.values.sm);
  const shouldUseListView = isMobileMatch || (isWidget && isWidgetBelowXsWidth);
  const { t } = useLocalization();
  const emptyLabel = t('common.emdash', '—');
  const authIsLoaded = useAuthIsLoaded();
  const isSingleAccountView = !!accountId;
  const isCustomGroup = isCustomGroupBy(groupBy);
  const isNative = Capacitor.getPlatform() !== 'web';
  const {
    createdDates,
    isLoading: areCreatedDatesLoading,
    isError: createdDatesFailed,
  } = useAccountCreatedDates(endDate);
  const {
    accountIds: inScopeAccountIds,
    balancesByAccountId,
    isLoading: isScopeLoading,
  } = useInScopeAccountIds({
    endDate,
    skipScope: isSingleAccountView,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    excludedInstitutions,
  });

  const { data, isLoading, isFetching, error } =
    useGetAccountHistoricalComparisonQuery(
      authIsLoaded && startDate && endDate
        ? historicalComparisonQueryArg({
            startDate,
            endDate,
            groupBy,
            accountId,
            excludedAccounts,
            excludedAccountTypes,
            excludedCategories,
            excludedInstitutions,
          })
        : skipToken,
      {
        skip: !authIsLoaded || !startDate || !endDate,
        refetchOnMountOrArgChange: true,
      },
    );

  const { rows, groupTotalsByName } = useMemo(() => {
    const sourceRecords =
      areCreatedDatesLoading || createdDatesFailed ? [] : (data?.Records ?? []);
    const records = isSingleAccountView
      ? sourceRecords
      : filterByInScopeIds(
          sourceRecords,
          (record) => record.Id,
          inScopeAccountIds,
        );

    const activeRecords = records
      .filter((record) => !record.ClosedDate)
      .map((record) =>
        withHistoricalChangeForRange(
          withLiveEndingBalance(
            record,
            lookupLiveEndingBalance(
              record.Id,
              balancesByAccountId,
              accountId,
              liveBalance,
            ),
            endDate,
          ),
          {
            createdDate: createdDates.get(record.Id),
            startDate,
            endDate,
          },
        ),
      );
    const emptyTotals: GroupTotalsByName = new Map();

    if (isSingleAccountView) {
      return {
        rows: activeRecords.map((record) => ({
          ...record,
          id: record.Id.toString(),
          groupName: '',
          groupSort: 0,
          isAsset: record.IsAsset,
          classificationSort: 0,
          sortIndex: 0,
        })),
        groupTotalsByName: emptyTotals,
      };
    }

    const getGroupName = (record: AccountHistoricalComparisonItem): string => {
      if (isCustomGroup) {
        return record.GroupName || 'Not Set';
      }
      switch (groupBy) {
        case 'institution':
          return record.InstitutionName || 'No Institution';
        case 'data source':
          // Data for this grouping is not available in this component's API response.
          // Falling back to classification grouping.
          return record.GroupName || 'Unclassified';
        case 'none':
        default:
          return record.GroupName || 'Unclassified';
      }
    };

    const groupedRecords = new Map<string, AccountHistoricalComparisonItem[]>();
    for (const record of activeRecords) {
      const groupName = getGroupName(record);
      const bucket = groupedRecords.get(groupName);
      if (bucket) {
        bucket.push(record);
      } else {
        groupedRecords.set(groupName, [record]);
      }
    }

    const allRows: TreeGridRow[] = [];
    const nextTotals: GroupTotalsByName = new Map();
    const sortedGroupKeys = [...groupedRecords.keys()].sort((a, b) =>
      a.localeCompare(b),
    );

    for (const groupName of sortedGroupKeys) {
      const groupRecords = groupedRecords.get(groupName);
      if (!groupRecords) continue;

      groupRecords.forEach((record, index) => {
        allRows.push({
          ...record,
          id: record.Id.toString(),
          groupName,
          groupSort: index,
          isAsset: record.IsAsset,
          classificationSort: index,
          sortIndex: index,
          isGroupTotal: false,
          OldDollarBalance: record.OldDollarBalance,
          DollarBalance: record.DollarBalance,
          DollarChange: record.DollarChange,
          PercentChange: record.PercentChange,
          path: [groupName, `${record.Name}_${String(record.Id)}`],
        });
      });

      nextTotals.set(groupName, {
        ...aggregateHistoricalComparisons(groupRecords),
        count: groupRecords.length,
      });
    }

    return { rows: allRows, groupTotalsByName: nextTotals };
  }, [
    areCreatedDatesLoading,
    createdDates,
    createdDatesFailed,
    data,
    accountId,
    balancesByAccountId,
    endDate,
    inScopeAccountIds,
    isSingleAccountView,
    liveBalance,
    groupBy,
    isCustomGroup,
    startDate,
  ]);

  const grandTotal = useMemo(() => {
    if (!rows.length || isSingleAccountView) return null;

    return aggregateHistoricalComparisons(rows);
  }, [rows, isSingleAccountView]);

  const columns: GridColDef<TreeGridRow>[] = useMemo(
    () => [
      createNameColumn(t),
      createBalanceColumn(
        'OldDollarBalance',
        startDate
          ? `${formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t(
              'grid.balance',
              'Value',
            )}`
          : '',
        groupTotalsByName,
        emptyLabel,
      ),
      createBalanceColumn(
        'DollarBalance',
        endDate
          ? `${formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t(
              'grid.balance',
              'Value',
            )}`
          : '',
        groupTotalsByName,
        emptyLabel,
      ),
      createBalanceColumn(
        'DollarChange',
        t('grid.dollarChange', '$ Change'),
        groupTotalsByName,
        emptyLabel,
      ),
      createPercentageColumn(t, groupTotalsByName, emptyLabel),
    ],
    [emptyLabel, startDate, endDate, t, groupTotalsByName],
  );

  const listViewColDef: GridListViewColDef<TreeGridRow> = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: (
        params: GridRenderCellParams<
          TreeGridRow,
          string | number | boolean | null
        >,
      ) => {
        const { row, rowNode, api } = params;
        const visibilityModel = api.state.columns.columnVisibilityModel;
        const isFieldVisible = (field: string) =>
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
          visibilityModel[field] !== false;

        if (rowNode.type === 'group') {
          const isExpanded = !!rowNode.childrenExpanded;
          const groupLabel =
            groupKeyLabel(rowNode.groupingKey) || toDisplayString(params.value);

          const descendantCount = groupDescendantCount(rowNode);

          const groupTotalValue =
            getGroupTotals(groupTotalsByName, groupLabel)?.DollarBalance ??
            null;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            (api as GridApiPremium).setRowChildrenExpansion(
              params.id,
              !isExpanded,
            );
          };

          return (
            <Box
              sx={{
                width: '100%',
                px: 2,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <IconButton
                size='small'
                onClick={handleToggleExpand}
                sx={{ p: 0.25 }}
                aria-label={
                  isExpanded
                    ? t('grid.collapseGroup', 'Collapse group')
                    : t('grid.expandGroup', 'Expand group')
                }
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize='small' />
                ) : (
                  <ChevronRightIcon fontSize='small' />
                )}
              </IconButton>

              <Typography
                variant='body2'
                sx={{
                  fontWeight: '600 !important',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${groupLabel} (${String(descendantCount)})`}
              </Typography>

              <Typography
                variant='body2'
                sx={{ ml: 'auto', fontWeight: 700, whiteSpace: 'nowrap' }}
              >
                {formatHistoricalCurrency(groupTotalValue, emptyLabel)}
              </Typography>
            </Box>
          );
        }

        // Generated aggregation footers are not TreeGridRows and have no Name.
        if (rowNode.type !== 'leaf' || !isAccountListRow(row)) {
          return null;
        }

        const name = row.Name.trim();
        const oldValue = row.OldDollarBalance;
        const value = row.DollarBalance;
        const dollarChange = row.DollarChange;
        const percentChange = row.PercentChange;

        const oldLabel = startDate
          ? `${formatAppDate(startDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t('grid.balance', 'Value')}`
          : t('grid.balance', 'Value');
        const valueLabel = endDate
          ? `${formatAppDate(endDate, { format: APP_DATE_DISPLAY_FORMAT })} ${t('grid.balance', 'Value')}`
          : t('grid.balance', 'Value');

        const Line = (line: {
          left: string;
          right?: string;
          rightColor?: string;
          boldRight?: boolean;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, color: 'text.secondary' }}
            >
              {line.left}
            </Typography>
            {line.right != null ? (
              <Typography
                variant='body2'
                sx={{
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  textAlign: 'right',
                  color: line.rightColor ?? 'text.secondary',
                  ...(line.boldRight ? { fontWeight: '600 !important' } : null),
                }}
              >
                {line.right}
              </Typography>
            ) : null}
          </Box>
        );

        return (
          <Box
            sx={{
              width: '100%',
              px: 2,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {/* Line 1: Name + Current Value */}
            {(isFieldVisible('Name') || isFieldVisible('DollarBalance')) && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                <Typography
                  variant='body1'
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.2,
                    fontWeight: '600 !important',
                  }}
                >
                  {isFieldVisible('Name') ? name : valueLabel}
                </Typography>
                {isFieldVisible('DollarBalance') ? (
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: '600 !important', whiteSpace: 'nowrap' }}
                  >
                    {formatHistoricalCurrency(value, emptyLabel)}
                  </Typography>
                ) : null}
              </Box>
            )}

            {/* Line 2: Old Value */}
            {isFieldVisible('OldDollarBalance') && (
              <Line
                left={oldLabel}
                right={formatHistoricalCurrency(oldValue, emptyLabel)}
                rightColor={getValueColor(oldValue)}
              />
            )}

            {/* Line 3: $ Change */}
            {isFieldVisible('DollarChange') && (
              <Line
                left={t('grid.dollarChange', '$ Change')}
                right={formatHistoricalCurrency(dollarChange, emptyLabel)}
                rightColor={getValueColor(dollarChange)}
                boldRight={dollarChange != null && dollarChange !== 0}
              />
            )}

            {/* Line 4: % Change */}
            {isFieldVisible('PercentChange') && (
              <Line
                left={t('grid.percentChange', '% Change')}
                right={
                  percentChange == null
                    ? emptyLabel
                    : formatPercentValue(percentChange)
                }
                rightColor={getValueColor(percentChange)}
                boldRight={percentChange !== 0}
              />
            )}
          </Box>
        );
      },
    }),
    [emptyLabel, endDate, startDate, t, groupTotalsByName],
  );

  if (error || createdDatesFailed) {
    return (
      <Typography
        color='error'
        sx={{ p: 3 }}
      >
        {t('errorLoadingBalanceSheetData', 'Error loading balance sheet data')}
      </Typography>
    );
  }

  // Custom grouping cell component that displays group totals
  const CustomGroupingCell = (
    params: GridRenderCellParams<TreeGridRow, string | number | boolean | null>,
  ) => {
    if (params.row.isGroupTotal || params.rowNode.type !== 'group') {
      return null;
    }

    const groupName =
      groupKeyLabel(params.rowNode.groupingKey) ||
      toDisplayString(params.value);
    const groupTotals = getGroupTotals(groupTotalsByName, groupName);
    const groupingParams = {
      ...params,
      rowNode: groupTotals
        ? { ...params.rowNode, descendantCount: groupTotals.count }
        : params.rowNode,
    };

    return <GridTreeDataGroupingCell {...groupingParams} />;
  };

  return (
    <Box ref={widgetWidthRef}>
      <GridContainer
        isWidget={isWidget}
        size={size}
      >
        <DataGridPremium
          rows={rows}
          columns={columns}
          treeData={!isSingleAccountView}
          defaultGroupingExpansionDepth={-1}
          getTreeDataPath={
            !isSingleAccountView
              ? (row: TreeGridRow) => row.path || []
              : undefined
          }
          groupingColDef={
            !isSingleAccountView
              ? {
                  hideDescendantCount: false,
                  renderCell: CustomGroupingCell,
                }
              : undefined
          }
          showToolbar
          disablePivoting
          listView={shouldUseListView}
          {...(shouldUseListView ? { listViewColumn: listViewColDef } : {})}
          {...(shouldUseListView
            ? { getRowHeight: () => 'auto' as const }
            : {})}
          initialState={!isSingleAccountView ? INITIAL_GRID_STATE_GROUPED : {}}
          // Prevent the DataGrid from creating a pinned root aggregation footer
          // (it appears as a generated row in list view and is not a TreeGridRow).
          // Group totals stay inline; the custom footer already shows the grand total.
          getAggregationPosition={(groupNode: GridGroupNode | null) =>
            groupNode == null ? null : 'inline'
          }
          slots={{
            columnMenu: () => null,
            footer: () =>
              !isSingleAccountView ? (
                <CustomFooter
                  grandTotal={grandTotal}
                  emptyLabel={emptyLabel}
                />
              ) : null,
            ...(isNative
              ? {
                  toolbar: (props) => (
                    <CustomToolbarForNative
                      {...props}
                      excelWorker={createExcelExportWorker}
                    />
                  ),
                }
              : shouldUseListView
                ? { toolbar: XsPreferencesDrawerToolbar }
                : {}),
            ...(isNative
              ? {
                  toolbar: (props) => (
                    <CustomToolbarForNative
                      {...props}
                      excelWorker={createExcelExportWorker}
                    />
                  ),
                }
              : isMobileMatch
                ? { toolbar: XsPreferencesDrawerToolbar }
                : {}),
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
            ...(isMobileMatch
              ? { toolbar: { showQuickFilter: true } }
              : {
                  toolbar: {
                    ...defaultToolbarExportSlotProps,
                    showQuickFilter: true,
                    excelOptions: {
                      ...defaultToolbarExportSlotProps.excelOptions,
                      worker: createExcelExportWorker,
                    },
                  },
                }),
          }}
          disableColumnMenu
          disableRowSelectionOnClick
          loading={
            isLoading || isFetching || isScopeLoading || areCreatedDatesLoading
          }
          sx={(theme) => ({
            ...gridStyles,
            ...(isMobileMatch && {
              // In list view, force the single list column to actually fill the grid width.
              '& .MuiDataGrid-row:not(.MuiDataGrid-row--group) .MuiDataGrid-cellEmpty':
                {
                  display: 'none !important',
                },
              '& .MuiDataGrid-main': { width: '100%' },
              '& .MuiDataGrid-virtualScroller': { width: '100%' },
              '& .MuiDataGrid-virtualScrollerRenderZone': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                width: '100% !important',
              },
              '& .MuiDataGrid-virtualScrollerContent > div': {
                width: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-row': {
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
                flex: '1 1 0',
              },
              '& .MuiDataGrid-cellContent': {
                width: '100%',
                flex: 1,
                minWidth: 0,
              },
              [theme.breakpoints.down(800)]: {
                '& .MuiDataGrid-cell': {
                  width: '100% !important',
                  minWidth: '100% !important',
                  display: 'block !important',
                },
              },
            }),
          })}
        />
      </GridContainer>
    </Box>
  );
};

export default HistoricalGrid;
